// Atlas fallback city synthesizer (ATL-2). PURE (ENG-1).
//
// Any fetch failure (timeout / HTTP / CORS / empty, ATL-1) falls back to a
// deterministic Manhattan-ish grid city seeded from the location string, so an
// Atlas world ALWAYS generates something plausible offline. The result carries
// `simulated: true` for the UI badge. Same string ⇒ byte-identical city.
import { ATLAS } from '../config';
import { Rng } from '../engine/rng';
import {
  fnv1a, boundsOf, intermediateCitySchema,
  type IntermediateCity, type Polyline, type Polygon, type Building, type RoadClass,
} from './intermediate';

/** Synthesize a deterministic grid city for `location` at `scale` m/block (ATL-2). */
export function synthesizeCity(location: string, scale = 1): IntermediateCity {
  const rng = new Rng(fnv1a(location));
  const size = Math.max(140, Math.round(ATLAS.BBOX_M / scale / 2)); // half-extent, blocks
  // Grid spacing kept in a band that yields a ~10–25% road area after stroking.
  const spacing = Math.max(48, Math.min(96, Math.round((60 + rng.nextFloat() * 60) / scale)));

  const lines: number[] = [];
  for (let c = -size; c <= size; c += spacing) lines.push(c);

  const classFor = (c: number): RoadClass => {
    const d = Math.abs(c);
    if (d === 0) return 'primary';
    if (d <= spacing) return 'secondary';
    return 'residential';
  };
  const roads: Polyline[] = [];
  for (const c of lines) {
    roads.push({ points: [[c, -size], [c, size]], roadClass: classFor(c) });
    roads.push({ points: [[-size, c], [size, c]], roadClass: classFor(c) });
  }

  const buildings: Building[] = [];
  const green: Polygon[] = [];
  const water: Polygon[] = [];
  let biggestPark = -1, biggestArea = 0;

  for (let i = 0; i + 1 < lines.length; i++) {
    for (let j = 0; j + 1 < lines.length; j++) {
      const roadHalf = 5, setback = 2 + rng.nextInt(3);
      const ax = lines[i]! + roadHalf + setback, bx = lines[i + 1]! - roadHalf - setback;
      const az = lines[j]! + roadHalf + setback, bz = lines[j + 1]! - roadHalf - setback;
      if (bx - ax < 8 || bz - az < 8) continue;
      const rect: Polygon = { points: [[ax, az], [bx, az], [bx, bz], [ax, bz]] };

      if (rng.nextFloat() < 0.15) { // ~15% parks (ATL-2)
        green.push(rect);
        const area = (bx - ax) * (bz - az);
        if (area > biggestArea) { biggestArea = area; biggestPark = green.length - 1; }
        continue;
      }
      // Levels fall off from the centre (taller downtown, 2–8, ATL-2).
      const cx = (ax + bx) / 2, cz = (az + bz) / 2;
      const dist = Math.min(1, Math.hypot(cx, cz) / size);
      const levels = Math.max(2, Math.min(8, Math.round(8 - dist * 6 + (rng.nextFloat() - 0.5) * 2)));
      buildings.push({ footprint: rect.points, levels });
    }
  }

  // Guarantee at least one park (ATL-2), and a pond in the largest.
  if (biggestPark < 0 && buildings.length) {
    const b = buildings.pop()!;
    green.push({ points: b.footprint });
    biggestPark = green.length - 1;
  }
  if (biggestPark >= 0) {
    const bb = boundsOf(green[biggestPark]!.points);
    const w = bb.maxX - bb.minX, h = bb.maxZ - bb.minZ;
    water.push({ points: [
      [bb.minX + w * 0.3, bb.minZ + h * 0.3], [bb.minX + w * 0.7, bb.minZ + h * 0.3],
      [bb.minX + w * 0.7, bb.minZ + h * 0.7], [bb.minX + w * 0.3, bb.minZ + h * 0.7],
    ] });
  }

  return intermediateCitySchema.parse({
    version: 1, scale, sizeBlocks: size, simulated: true, label: location,
    roads, water, green, buildings, trees: [],
  });
}
